/* eslint-disable @typescript-eslint/no-explicit-any */
import CategorySort from '@/components/Category/CategorySort'
import ImageOptimize from '@/components/ImageOptimize'
import ProductCard from '@/components/ProductCard'
import ProductCardSkeleton from '@/components/Skeleton/ProductCardSkeleton'
import { PageType } from '@/constants'
import { useGetProductsByCategory } from '@/hooks/useProductHook'
import HeaderSearch from '@/layouts/HeaderSearch'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import {
  type Menu,
  type Product,
  type ProductResponse,
  type QueryParam,
} from '@/types'
import { getTranslate, setContext } from '@/utils/api-interceptors'
import {
  getBanner,
  getMenus,
  getProductWithPaginate,
  getProvinces,
  getQuickMenu,
  getSearchSuggestMenu,
} from '@/utils/endpoint'
import { useSearchParams } from '@/utils/next-util'
import { getCookies } from 'cookies-next'
import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { v4 as uuidv4 } from 'uuid'
export const sortTypes = [
  { label: 'Giá tăng dần', value: 'priceDesc' },
  { label: 'Giá giảm dần', value: 'priceAsc' },
  { label: 'Mới nhất', value: 'dateDesc' },
  { label: 'Bán chạy nhất', value: 'dateAsc' },
  { label: 'Tên: A – Z', value: 'alphaAsc' },
  { label: 'Tên: Z – A', value: 'alphaDesc' },
]
export const sortByTypes = [
  { label: 'Nên mua', type: 'custom', name: 'shouldBuy' },
  { label: 'Bán chạy', type: 'custom', name: 'bestSelling' },
  { label: 'Trả góp', type: 'custom', name: 'allowInstallment' },
  { label: 'Giá tăng dần', type: 'sortType', name: 'asc' },
  { label: 'Giá giảm dần', type: 'sortType', name: 'desc' },
]
const SearchPage = ({
  productSearchData,
}: {
  productSearchData: ProductResponse
}) => {
  const router = useRouter()
  const [suggestItems, setSuggestItems] = useState<Menu[]>([])
  const [, setTotalProduct] = useState<number>(productSearchData?.total || 0)
  const [products, setProducts] = useState<Product[]>(productSearchData?.data)
  const { getProducts } = useGetProductsByCategory()
  const [totalPages, setTotalPages] = useState(productSearchData?.pages)
  const [currentPage, setCurrentPage] = useState(1)
  const [routerChange, setRouterChange] = useState(false)
  const [loading, setLoading] = useState(false)
  const [currentParams, setCurrentParams] = useSearchParams([
    {
      name: 'page',
      value: null,
    },
    {
      name: 'query',
      value: null,
    },
    {
      name: 'sortBy',
      value: null,
    },
    {
      name: 'sortType',
      value: null,
    },
    {
      name: 'bestSelling',
      value: null,
    },
    {
      name: 'shouldBuy',
      value: null,
    },
    {
      name: 'allowInstallment',
      value: null,
    },
  ])
  useEffect(() => {
    if (productSearchData?.data) {
      setProducts(productSearchData?.data)
      setTotalProduct(productSearchData?.total)
    }
    return () => {
      setCurrentPage(1)
    }
  }, [productSearchData])
  const [sortByOptions] = useState(sortByTypes)
  const activeParams = currentParams
  const activeSort = activeParams.find(
    (filter: any) => filter.name === 'sortType'
  ).value
  const activeSortCustom = activeParams
    .filter((filter: any) => filter.value === 'true')
    .map((item: { name: string }) => item.name) || ['']

  const updateParams = useCallback(
    (params: any, routerChange = true) => {
      setLoading(true)
      setCurrentPage(1)
      setProducts([])
      if (routerChange) {
        setRouterChange(true)
      }
      const newFilters = activeParams.map((filter: any) => {
        const matchedParam = params?.find((p: any) => p.name === filter.name)
        return matchedParam ? { ...filter, value: matchedParam?.value } : filter
      })
      setCurrentParams(newFilters)
    },
    [activeParams]
  )

  useEffect(() => {
    if (!routerChange) {
      return
    }
    setLoading(true)
    getProducts(
      {
        limit: 12,
        currentPage: currentPage,
        sortBy: (router?.query.sortBy as string) || 'score',
        sortType: (router?.query.sortType as string) || 'desc',
        search: router?.query?.query,
      },
      {
        onSuccess: res => {
          setProducts(res.data.data)
          setLoading(false)
          setTotalPages(res.data.pages)
          setTotalProduct(res.data.total)
        },
      }
    )
  }, [router.query])
  const fetchMoreProducts = () => {
    setCurrentPage(currentPage + 1)
    setLoading(true)
    getProducts(
      {
        limit: 12,
        currentPage: currentPage + 1,
        sortBy: (router?.query.sortBy as string) || 'score',
        sortType: (router?.query.sortType as string) || 'desc',
        search: router?.query?.query,
      },
      {
        onSuccess: res => {
          setProducts(products.concat(res.data.data))
          setLoading(false)
          setTotalPages(res.data.pages)
        },
      }
    )
  }

  useEffect(() => {
    getSearchSuggestMenu().then(res => {
      if (res) setSuggestItems(res)
    })
  }, [])

  return (
    <Main
      meta={<Meta title={'Tìm kiếm'} description={''} />}
      pageLayout={PageType.SEARCH}
    >
      <HeaderSearch />
      <section className='container pb-[120px]'>
        <div className='flex w-full mt-2 gap-x-3 text-center overflow-y-hidden overflow-x-auto hiddenScrollBar mb-2 min-h-5'>
          {suggestItems.map((e, index) => (
            <span
              onClick={() => {
                return router.push(
                  `/search-result?query=${getTranslate(e.name)}`
                )
              }}
              key={`keyword-${index}`}
              className='text-sm inline-block whitespace-nowrap text-dark-800 cursor-pointer'
            >
              {getTranslate(e.name)}
            </span>
          ))}
        </div>
        <h3 className='mb-2.5 font-bold text-base text-dark-800'>
          Kết quả tìm kiếm{' '}
          <span className='font-normal'>“{router?.query?.query}”</span>
        </h3>
        <div className='mb-2 mt-1.5'>
          <CategorySort
            sortOptions={sortByOptions}
            onChange={updateParams}
            activeSort={activeSort}
            activeSortCustom={activeSortCustom}
          />
        </div>
        {loading && (
          <div className='products grid grid-cols-2 gap-3 pb-[120px]'>
            {[...new Array(8)].map(() => (
              <ProductCardSkeleton key={uuidv4()} />
            ))}
          </div>
        )}
        {products && products.length > 0 ? (
          <InfiniteScroll
            className='pb-[30px]'
            dataLength={products.length}
            next={fetchMoreProducts}
            hasMore={
              currentPage !== Number(totalPages) || Number(totalPages) === 0
            }
            loader=''
          >
            <div className='productData grid grid-cols-2 gap-3'>
              {products.map((product: Product, idx: number) => (
                <ProductCard
                  layout={2}
                  product={product}
                  key={product?._id + idx}
                />
              ))}
            </div>
          </InfiniteScroll>
        ) : (
          ''
        )}
        {products && products.length == 0 && !loading && (
          <div className='mt-[100px] h-full flex flex-col items-center justify-center'>
            <ImageOptimize
              src='/assets/images/no-product.svg'
              alt={'Không tìm thấy sản phẩm phù hợp với bạn'}
              width={124}
              height={145}
            />
            <span className='mt-[20px]text-HeadlineMedium w-[200px] text-center font-normal'>
              Không tìm thấy sản phẩm tương ứng
            </span>
          </div>
        )}
      </section>
    </Main>
  )
}

export default SearchPage

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req, res, query } = context
  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: context.locale,
    }
  )

  const params = {
    ...query,
    page: 1,
    fullTextSearch: query?.query,
    sortBy: query?.sortBy ? query?.sortBy : 'score',
    sortType: query?.sortType ? query?.sortType : 'desc',
    limit: 12,
  }
  const [stores, menus, banner, quickMenu, productSearchData] =
    await Promise.all([
      getProvinces(),
      getMenus(),
      getBanner(),
      getQuickMenu(),
      getProductWithPaginate(params as QueryParam),
    ])

  return {
    props: {
      stores,
      menus,
      banner,
      language,
      messages,
      quickMenu,
      productSearchData,
      token: token || null,
    },
  }
}
